import { useState, useEffect } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Input,
  Button,
  Table,
  Space,
  Typography,
  Tag,
  DatePicker,
  message,
  Alert,
  Statistic,
  Row,
  Col,
  Select,
  Modal
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  FilterOutlined,
  EyeOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { PageHeader } from '../components/PageHeader';
import { api, ProfitEntry } from '../api';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { TextArea } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const AdminProfitEntry = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allocating, setAllocating] = useState<string | null>(null);
  const [profitEntries, setProfitEntries] = useState<ProfitEntry[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [pageTotals, setPageTotals] = useState({ gross_amount_cents: 0 });
  
  // Filter state
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [distributedFilter, setDistributedFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [minAmount, setMinAmount] = useState<number | undefined>();
  const [maxAmount, setMaxAmount] = useState<number | undefined>();

  useEffect(() => {
    fetchProfitEntries();
  }, []);

  const fetchProfitEntries = async (page = 1) => {
    try {
      setLoading(true);
      const data = await api.profitEntries.list({
        from: dateRange[0]?.format('YYYY-MM-DD'),
        to: dateRange[1]?.format('YYYY-MM-DD'),
        distributed: distributedFilter,
        min_amount: minAmount,
        max_amount: maxAmount,
        page,
        per: pagination.pageSize,
      });
      
      setProfitEntries(data.profit_entries);
      setPagination({
        current: page,
        pageSize: data.pagination.per_page,
        total: data.pagination.total_count,
      });
      setPageTotals(data.page_totals);
    } catch (error: any) {
      console.error('Failed to fetch profit entries:', error);
      message.error('Failed to load profit entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      // Convert dollars to cents
      const grossAmountCents = Math.round(values.gross_amount_dollars * 100);

      const payload: any = {
        gross_amount_cents: grossAmountCents,
      };

      if (values.notes) {
        payload.notes = values.notes;
      }

      if (values.entry_date) {
        payload.entry_date = values.entry_date.format('YYYY-MM-DD');
      }

      await api.profitEntries.create(payload);
      
      message.success('Profit entry created successfully');
      form.resetFields();
      fetchProfitEntries();
    } catch (error: any) {
      console.error('Failed to create profit entry:', error);
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errors.forEach((err: string) => message.error(err));
        } else {
          message.error(JSON.stringify(errors));
        }
      } else {
        message.error(error.response?.data?.error || 'Failed to create profit entry');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAllocate = async (entryId: string) => {
    Modal.confirm({
      title: 'Allocate Profit',
      content: 'Are you sure you want to allocate this profit entry to eligible users? This action cannot be undone.',
      onOk: async () => {
        try {
          setAllocating(entryId);
          const result = await api.profitEntries.allocate(entryId);
          
          // Handle different allocation types
          if (result.allocations && result.allocations.length > 0) {
            message.success(`Successfully allocated to ${result.allocations.length} users`);
          } else if (result.allocations_count !== undefined) {
            message.success(`Successfully allocated to ${result.allocations_count} users`);
          } else {
            message.success('Allocation completed successfully');
          }
          
          fetchProfitEntries();
        } catch (error: any) {
          console.error('Failed to allocate profit:', error);
          message.error(error.response?.data?.error || 'Failed to allocate profit');
        } finally {
          setAllocating(null);
        }
      },
    });
  };

  const handleViewAllocations = (entryId: string) => {
    navigate(`/admin/profits/${entryId}/allocations`);
  };

  const handleFilterApply = () => {
    fetchProfitEntries(1);
  };

  const handleFilterReset = () => {
    setDateRange([null, null]);
    setDistributedFilter('all');
    setMinAmount(undefined);
    setMaxAmount(undefined);
    // Will trigger fetchProfitEntries via useEffect
    setTimeout(() => fetchProfitEntries(1), 0);
  };

  const handleTableChange = (newPagination: any) => {
    fetchProfitEntries(newPagination.current);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const disabledDate = (current: Dayjs) => {
    // Disable dates before today
    return current && current < dayjs().startOf('day');
  };

  const columns = [
    {
      title: 'Entry Date',
      dataIndex: 'entry_date',
      key: 'entry_date',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a: ProfitEntry, b: ProfitEntry) => 
        dayjs(a.entry_date).unix() - dayjs(b.entry_date).unix(),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY HH:mm'),
      sorter: (a: ProfitEntry, b: ProfitEntry) => 
        dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Gross Amount',
      dataIndex: 'gross_amount_cents',
      key: 'gross_amount_cents',
      render: (cents: number) => (
        <Text strong style={{ color: '#3f8600' }}>
          {formatCurrency(cents)}
        </Text>
      ),
      sorter: (a: ProfitEntry, b: ProfitEntry) => a.gross_amount_cents - b.gross_amount_cents,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || <Text type="secondary">—</Text>,
    },
    {
      title: 'Entered By',
      dataIndex: 'entered_by',
      key: 'entered_by',
      render: (enteredBy: any) => enteredBy ? `${enteredBy.name} (${enteredBy.email})` : '—',
    },
    {
      title: 'Status',
      dataIndex: 'distributed',
      key: 'distributed',
      render: (distributed: boolean) => (
        <Tag color={distributed ? 'green' : 'orange'} icon={distributed ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
          {distributed ? 'DISTRIBUTED' : 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ProfitEntry) => (
        <Space>
          {!record.distributed && (
            <Button
              type="primary"
              size="small"
              icon={<ThunderboltOutlined />}
              loading={allocating === record.id}
              onClick={() => handleAllocate(record.id)}
            >
              Allocate
            </Button>
          )}
          {record.distributed && (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewAllocations(record.id)}
            >
              View Allocations
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        title="Profit Entry"
        description="Record daily gross profit for allocation"
      />

      <Alert
        title="Profit Entry Guidelines"
        description={
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>Enter the gross daily profit amount before any fees or allocations</li>
            <li>No backdating allowed - all entries are timestamped with current date/time</li>
            <li>Multiple entries per day are allowed and will be totaled in history</li>
            <li>Zero amounts are permitted to explicitly record no-profit days</li>
            <li>Negative amounts require adjusting entry with reason (use Void button for corrections)</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Summary Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Page Total Gross Profit"
              value={pageTotals.gross_amount_cents / 100}
              precision={2}
              prefix={<DollarOutlined />}
              styles={{ content: { color: '#3f8600' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Total Entries"
              value={pagination.total}
              styles={{ content: { color: '#1677ff' } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card title="Filters" style={{ marginBottom: 24 }} extra={<FilterOutlined />}>
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <label style={{ display: 'block', marginBottom: 8 }}>Date Range</label>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [null, null])}
                format="MMM D, YYYY"
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} md={6}>
              <label style={{ display: 'block', marginBottom: 8 }}>Status</label>
              <Select
                value={distributedFilter}
                onChange={setDistributedFilter}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All</Select.Option>
                <Select.Option value="yes">Distributed</Select.Option>
                <Select.Option value="no">Pending</Select.Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <label style={{ display: 'block', marginBottom: 8 }}>Min Amount ($)</label>
              <InputNumber
                value={minAmount}
                onChange={(val) => setMinAmount(val || undefined)}
                style={{ width: '100%' }}
                precision={2}
                placeholder="0.00"
              />
            </Col>
            <Col xs={24} md={6}>
              <label style={{ display: 'block', marginBottom: 8 }}>Max Amount ($)</label>
              <InputNumber
                value={maxAmount}
                onChange={(val) => setMaxAmount(val || undefined)}
                style={{ width: '100%' }}
                precision={2}
                placeholder="0.00"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button type="primary" onClick={handleFilterApply} icon={<FilterOutlined />}>
                  Apply Filters
                </Button>
                <Button onClick={handleFilterReset}>
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Entry Form */}
      <Card 
        title="Create New Profit Entry" 
        style={{ marginBottom: 24 }}
        extra={<PlusOutlined />}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            entry_date: dayjs(),
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="gross_amount_dollars"
                label="Gross Amount (USD)"
                rules={[
                  { required: true, message: 'Please enter the gross amount' },
                ]}
              >
                <InputNumber
                  prefix="$"
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  precision={2}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="entry_date"
                label="Entry Date"
                tooltip="Cannot be earlier than today. Defaults to today if not specified."
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={disabledDate}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes (Optional)"
            tooltip="Add any relevant context or details about this profit entry"
          >
            <TextArea
              rows={3}
              placeholder="e.g., Revenue source, special circumstances, corrections, etc."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<PlusOutlined />}
                size="large"
              >
                Create Entry
              </Button>
              <Button
                htmlType="reset"
                onClick={() => form.resetFields()}
                disabled={submitting}
              >
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* History Table */}
      <Card title="Profit Entry History">
        <Table
          columns={columns}
          dataSource={profitEntries}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default AdminProfitEntry;
